/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import { format, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { PACE_HORIZON_SELECTION } from '../constants';
import { selectHotelFocusOn as selectSummaryHotelFocusOn } from '../components/tab-container/summary-tab/selectors';
import { selectHotelFocusOn as selectWorkbookHotelFocusOn } from '../components/tab-container/workbook-report/selectors';
import { selectHotelFocusOn as selectPaceHotelFocusOn } from '../components/tab-container/pace-tabs/selectors';
import { selectTrendHotelFocusOn } from '../components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectInvalidPrimaryHotelList,
  selectTabList,
  selectTabValue,
} from '../selectors';
import { dashboardActions } from '../slice';
import { workbookActions } from '../components/tab-container/workbook-report/slice';
import { trendActions } from '../components/tab-container/trend-tabs/slice';
import { paceActions } from '../components/tab-container/pace-tabs/slice';
import calculateComparisonDates from '../../common/utils/dateComparisonUtils';
import { YEAR_MONTH_DAY_PATTERN } from '../../common/constants/date-range';

/**
 * Custom hook that sets the `isSecondaryHotelSetOverTime` flag based on the selected tab
 * and whether the hotel matches the focused hotel in that tab.
 *
 * @returns {(isChecked: boolean) => void} A function that dispatches the flag update
 */
export const useSetSecondaryHotelSetOverTime = () => {
  const dispatch = useDispatch();
  //
  const summaryHotelFocusOn = useSelector(selectSummaryHotelFocusOn);
  const workbookHotelFocusOn = useSelector(selectWorkbookHotelFocusOn);
  const trendHotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const paceHotelFocusOn = useSelector(selectPaceHotelFocusOn);
  const selectedHotelName = useSelector(selectHotelName);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const tabValue = useSelector(selectTabValue);
  const TAB_VALUE = useSelector(selectTabList);

  /**
   * Updates Redux store with `isSecondaryHotelSetOverTime` based on the current tab and focused hotel.
   * @param {boolean} isChecked - The value to set for the `isSecondaryHotelSetOverTime` flag
   */
  return (isChecked) => {
    switch (tabValue) {
      case TAB_VALUE.SUMMARY:
        if (summaryHotelFocusOn?.id === selectedHotelName?.id) {
          dispatch(dashboardActions.setIsSecondaryHotelSetOverTime(isChecked));
          dispatch(dashboardActions.setInvalidSecondaryHotelList(invalidPrimaryHotels));
        }
        break;
      case TAB_VALUE.WORKBOOK_REPORT:
        if (workbookHotelFocusOn?.id === selectedHotelName?.id) {
          dispatch(workbookActions.setIsSecondaryHotelSetOverTime(isChecked));
          dispatch(workbookActions.setInvalidSecondaryHotelList(invalidPrimaryHotels));
        }
        break;
      case TAB_VALUE.PBI_TREND:
      case TAB_VALUE.TREND_TIME:
        if (trendHotelFocusOn?.id === selectedHotelName?.id) {
          dispatch(trendActions.setIsSecondaryHotelSetOverTime(isChecked));
          dispatch(trendActions.setInvalidSecondaryHotelList(invalidPrimaryHotels));
        }
        break;
      case TAB_VALUE.PACE:
      case TAB_VALUE.PACE_TEST:
        if (paceHotelFocusOn?.id === selectedHotelName?.id) {
          dispatch(paceActions.setIsSecondaryHotelSetOverTime(isChecked));
          dispatch(paceActions.setInvalidSecondaryHotelList(invalidPrimaryHotels));
        }
        break;
      default:
        break;
    }
  };
};

/**
 * Validates whether each hotel in the list has available data for the target and comparison date ranges.
 *
 * @param {Array<Object>} hotels - List of hotel objects with `dataAvailableRanges`, `databricksId`, `label`, `id`, `hotelCode`
 * @param {{ startDate: string, endDate: string }} targetDate - Target date range to validate
 * @param {{ startDate?: string, endDate?: string }} comparisonDate - Optional comparison date range
 * @returns {{ invalidHotels: Array<Object>, anyFailed: boolean }} List of invalid hotels and overall failure flag
 */
export const getHotelValidationResult = (hotels, targetDate, comparisonDate) => {
  const invalidHotels = [];
  let anyFailed = false;
  //
  hotels?.forEach((hotel) => {
    const minDate = hotel?.dataAvailableMinDate ? new Date(hotel.dataAvailableMinDate) : null;
    const maxDate = hotel?.dataAvailableMaxDate ? new Date(hotel.dataAvailableMaxDate) : null;
    const targetStart = new Date(targetDate.startDate);
    const targetEnd = new Date(targetDate.endDate);
    //
    const compStart = comparisonDate?.startDate ? new Date(comparisonDate.startDate) : null;
    const compEnd = comparisonDate?.endDate ? new Date(comparisonDate.endDate) : null;
    //
    const isWithinRange = (start, end) => {
      if (!minDate || !maxDate) return false;
      return start >= minDate && end <= maxDate;
    };
    const isWithinTargetRange = isWithinRange(targetStart, targetEnd);
    const isWithinComparisonTargetRange =
      compStart && compEnd ? isWithinRange(compStart, compEnd) : true;
    //
    const failed = !(isWithinTargetRange && isWithinComparisonTargetRange);
    //
    if (failed) {
      anyFailed = true;
      invalidHotels.push({
        databricksId: hotel.databricksId,
        label: hotel.label,
        id: hotel.id,
        hotelCode: hotel.hotelCode,
      });
    }
  });
  return { invalidHotels, anyFailed };
};
//
// TODO: remove after hotel set over time test
// const calculateComparisonDate = (timeComparison, isAligned, stayDate, customDate) => {
//   const startDate = new Date(stayDate?.startDate);
//   const endDate = new Date(stayDate?.endDate);

//   let offsetStartDate;
//   let offsetEndDate;

//   switch (timeComparison) {
//     case compareWindows.otb:
//       offsetStartDate = startDate;
//       offsetEndDate = endDate;
//       break;

//     case compareWindows.pw:
//       offsetStartDate = subDays(startDate, 7);
//       offsetEndDate = subDays(endDate, 7);
//       break;

//     case compareWindows.pm:
//       if (isAligned) {
//         offsetStartDate = subDays(startDate, 28);
//         offsetEndDate = subDays(endDate, 28);
//       } else {
//         offsetStartDate = subMonths(startDate, 1);
//         offsetEndDate = subMonths(endDate, 1);
//       }
//       break;

//     case compareWindows.py:
//       if (isAligned) {
//         offsetStartDate = subDays(startDate, 364);
//         offsetEndDate = subDays(endDate, 364);
//       } else {
//         offsetStartDate = subYears(startDate, 1);
//         offsetEndDate = subYears(endDate, 1);
//       }
//       break;

//     case compareWindows.cus:
//       offsetStartDate = customDate.startDate;
//       offsetEndDate = customDate.endDate;
//       break;

//     default:
//       break;
//   }
//   return {
//     startDate: offsetStartDate,
//     endDate: offsetEndDate,
//   };
// };
//
export const evaluateHotelDateValidation = ({
  tabValue,
  selectedHotelName,
  summaryHotelFocusOn,
  workbookHotelFocusOn,
  trendHotelFocusOn,
  paceHotelFocusOn,
  targetDate,
  trendTargetDate,
  alignmentToggle,
  summaryDateComparison,
  trendDateComparison,
  trendAlignmentToggle,
  paceDateComparison,
  paceAlignmentToggle,
  workbookAlignmentToggle,
  workbookDateComparison,
  summaryCustomStartDate,
  summaryCustomEndDate,
  trendTimeCustomStartDate,
  trendTimeCustomEndDate,
  workbookCustomStartDate,
  workbookCustomEndDate,
  dashboardAction,
  workbookAction,
  trendAction,
  paceAction,
  dispatch,
  summaryBookingDate,
  regionalMapBookingDate,
  trendBookingDate,
  paceBookingDate,
  workbookBookingDate,
  summarySymmetricComparisonToggle,
  paceSymmetricComparisonToggle,
  trendSymmetricComparisonToggle,
  workBookSymmetricComparisonToggle,
  isSummaryCustomBooking,
  isRegionCustomBooking,
  isTrendCustomBooking,
  isWorkbookCustomBooking,
  bookingPaceWindow,
  resetBookingDate,
  partialHours,
  lastBookingDate,
  TAB_VALUE,
}) => {
  let selectedTabFocusOn;
  let selectedTargetDate;
  let selectedBookingDate;
  let timeComparison;
  let alignment;
  let customDate = null;
  let actionName;
  let symmetricComparisonToggle;
  let isCustomBooking;
  // Resolve tab-specific focus & configuration
  switch (tabValue) {
    case TAB_VALUE.SUMMARY:
    case TAB_VALUE.ALERT:
    case TAB_VALUE.SEARCH:
      selectedTabFocusOn = summaryHotelFocusOn;
      selectedTargetDate = targetDate;
      selectedBookingDate = summaryBookingDate;
      timeComparison = summaryDateComparison;
      symmetricComparisonToggle = summarySymmetricComparisonToggle;
      alignment = alignmentToggle;
      customDate = { startDate: summaryCustomStartDate, endDate: summaryCustomEndDate };
      actionName = dashboardAction;
      isCustomBooking = isSummaryCustomBooking;
      break;

    case TAB_VALUE.WORKBOOK_REPORT:
    case TAB_VALUE.EXPORT_WORKBOOK_REPORT:
      selectedTabFocusOn = workbookHotelFocusOn;
      selectedTargetDate = targetDate;
      selectedBookingDate = workbookBookingDate;
      timeComparison = workbookDateComparison;
      alignment = workbookAlignmentToggle;
      symmetricComparisonToggle = workBookSymmetricComparisonToggle;
      customDate = { startDate: workbookCustomStartDate, endDate: workbookCustomEndDate };
      actionName = workbookAction;
      isCustomBooking = isWorkbookCustomBooking;
      break;
    case TAB_VALUE.PBI_TREND:
    case TAB_VALUE.TREND_TIME:
    case TAB_VALUE.MUI_TREND_TIME:
      selectedTabFocusOn = trendHotelFocusOn;
      selectedTargetDate = trendTargetDate;
      selectedBookingDate = trendBookingDate;
      timeComparison = trendDateComparison;
      symmetricComparisonToggle = trendSymmetricComparisonToggle;
      alignment = trendAlignmentToggle;
      customDate = { startDate: trendTimeCustomStartDate, endDate: trendTimeCustomEndDate };
      actionName = trendAction;
      isCustomBooking = isTrendCustomBooking;
      break;

    case TAB_VALUE.PACE:
    case TAB_VALUE.PACE_TEST:
      selectedTabFocusOn = paceHotelFocusOn;
      selectedTargetDate = targetDate;
      selectedBookingDate = paceBookingDate;
      symmetricComparisonToggle = paceSymmetricComparisonToggle;
      timeComparison = paceDateComparison;
      alignment = paceAlignmentToggle;
      actionName = paceAction;
      isCustomBooking = bookingPaceWindow === PACE_HORIZON_SELECTION.BOOKING_PACE_WINDOW;
      break;

    case TAB_VALUE.REGION_MAP:
      selectedTargetDate = targetDate;
      selectedBookingDate = regionalMapBookingDate;
      isCustomBooking = isRegionCustomBooking;
      break;

    default:
      break;
  }
  if (!isCustomBooking) {
    const today = startOfDay(new Date());
    const nearStay = startOfDay(new Date(selectedTargetDate?.startDate));
    const farStay = selectedTargetDate?.endDate
      ? startOfDay(new Date(selectedTargetDate.endDate))
      : null;
    //
    let newBookingDate;
    if (isBefore(nearStay, today) || isEqual(nearStay, today)) {
      if (farStay && isBefore(farStay, today)) {
        // Near < today AND Far < today
        newBookingDate = { ...selectedBookingDate, startDate: nearStay, endDate: farStay };
      } else {
        // Near <= today AND Far > today OR Far unknown
        newBookingDate = { ...selectedBookingDate, startDate: nearStay, endDate: today };
      }
    } else if (isAfter(nearStay, today) && farStay) {
      // Near > today (Far also > today) → use Far as End Date
      newBookingDate = { ...selectedBookingDate, startDate: today, endDate: farStay };
    }
    selectedBookingDate = newBookingDate;
    //
  }
  const start = format(new Date(selectedBookingDate.startDate), YEAR_MONTH_DAY_PATTERN);
  const end = format(new Date(selectedBookingDate.endDate), YEAR_MONTH_DAY_PATTERN);
  dispatch(
    dashboardAction.setSameStoreComparisonDate({
      startDate: start,
      endDate: end,
    })
  );
  //
  const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
    isCustomBooking ? selectedTargetDate : selectedBookingDate,
    selectedBookingDate,
    alignment,
    symmetricComparisonToggle,
    timeComparison,
    customDate,
    resetBookingDate,
    partialHours,
    lastBookingDate
  );
  const comparisonDates = isCustomBooking ? adjustedBookingDate : adjustedStayDate;
  // ---- PRIMARY HOTEL ----
  if (selectedHotelName && 'hotels' in selectedHotelName && !selectedHotelName?.isPredefined) {
    const { invalidHotels, anyFailed: hotelCheckFail } = getHotelValidationResult(
      selectedHotelName.hotels,
      selectedBookingDate,
      comparisonDates
    );
    dispatch(dashboardAction.setIsPrimaryHotelSetOverTimeDisabled(!hotelCheckFail));
    dispatch(dashboardAction.setInvalidPrimaryHotelList(invalidHotels));

    if (tabValue !== TAB_VALUE.REGION_MAP && selectedHotelName?.id === selectedTabFocusOn?.id) {
      dispatch(actionName.setIsSecondaryHotelSetOverTimeDisabled(!hotelCheckFail));
      dispatch(actionName.setInvalidSecondaryHotelList(invalidHotels));
    }
  }

  if (selectedHotelName && !('hotels' in selectedHotelName)) {
    dispatch(dashboardAction.setIsPrimaryHotelSetOverTimeDisabled(false));
    dispatch(dashboardAction.setIsPrimaryHotelSetOverTime(false));
  }

  if (selectedHotelName?.isPredefined) {
    dispatch(dashboardAction.setIsPrimaryHotelSetOverTimeDisabled(true));
    dispatch(dashboardAction.setIsPrimaryHotelSetOverTime(false));
  }

  // ---- SECONDARY HOTEL ----
  if (selectedTabFocusOn && 'hotels' in selectedTabFocusOn && !selectedTabFocusOn?.isPredefined) {
    if (tabValue !== TAB_VALUE.REGION_MAP && selectedHotelName?.id !== selectedTabFocusOn?.id) {
      const { invalidHotels, anyFailed: hotelCheckFail } = getHotelValidationResult(
        selectedTabFocusOn.hotels,
        selectedBookingDate,
        comparisonDates
      );
      dispatch(actionName.setIsSecondaryHotelSetOverTimeDisabled(!hotelCheckFail));
      dispatch(actionName.setInvalidSecondaryHotelList(invalidHotels));
    }
  }

  if (selectedTabFocusOn && !('hotels' in selectedTabFocusOn)) {
    dispatch(actionName.setIsSecondaryHotelSetOverTimeDisabled(true));
    dispatch(actionName.setIsSecondaryHotelSetOverTime(false));
  }

  if (selectedTabFocusOn?.isPredefined) {
    dispatch(actionName.setIsSecondaryHotelSetOverTimeDisabled(true));
    dispatch(actionName.setIsSecondaryHotelSetOverTime(false));
  }
};
